import React from 'react'

/**
 * App-wide crash guard.
 *
 * React only supports error boundaries as class components — there is no hook
 * equivalent — so this is intentionally the one class component in the app.
 * It catches render/lifecycle errors from anywhere in the tree below it and
 * shows a branded recovery screen instead of a blank white page.
 *
 * It does NOT catch: event-handler errors, async/Promise rejections, or errors
 * in the boundary itself. Those still need local try/catch + toast handling.
 * See Docs/ARCHITECTURE.md "Error handling".
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    // Surface it in the console for now. When real error reporting (Sentry, etc.)
    // is added, this is the single place to forward it — see Docs/TECH_DEBT.md.
    console.error('Uncaught error caught by ErrorBoundary:', error, errorInfo) // allowed-debug
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (!this.state.hasError) return this.props.children

    const isDev = process.env.NODE_ENV === 'development'

    return (
      <div style={styles.wrap}>
        <div style={styles.card}>
          <div style={styles.badge}>!</div>
          <h1 style={styles.title}>Something went wrong</h1>
          <p style={styles.body}>
            The app hit an unexpected error and couldn&apos;t finish loading this
            screen. Your data is safe — nothing was changed. Reloading usually
            fixes it.
          </p>
          <button style={styles.button} onClick={this.handleReload}>
            Reload the app
          </button>
          <p style={styles.subtle}>
            If this keeps happening, let John or Matt know what you were doing
            when it appeared.
          </p>

          {isDev && this.state.error && (
            <pre style={styles.devError}>
              {this.state.error.toString()}
            </pre>
          )}
        </div>
      </div>
    )
  }
}

const styles = {
  wrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: 24,
    backgroundColor: '#f9fafb',
    fontFamily: "'DM Sans', sans-serif",
  },
  card: {
    maxWidth: 440,
    width: '100%',
    backgroundColor: '#ffffff',
    borderRadius: 16,
    border: '1px solid #e5e7eb',
    boxShadow: '0 4px 24px rgba(26, 46, 26, 0.08)',
    padding: '40px 32px',
    textAlign: 'center',
  },
  badge: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    fontSize: 30,
    fontWeight: 700,
    lineHeight: '56px',
    margin: '0 auto 20px',
  },
  title: {
    margin: '0 0 12px',
    fontSize: 22,
    fontWeight: 700,
    color: '#1a2e1a',
  },
  body: {
    margin: '0 0 24px',
    fontSize: 15,
    lineHeight: 1.6,
    color: '#6b7c6b',
  },
  button: {
    display: 'inline-block',
    padding: '12px 28px',
    fontSize: 15,
    fontWeight: 600,
    fontFamily: 'inherit',
    color: '#ffffff',
    backgroundColor: '#2d4a2d',
    border: 'none',
    borderRadius: 10,
    cursor: 'pointer',
  },
  subtle: {
    margin: '20px 0 0',
    fontSize: 13,
    lineHeight: 1.5,
    color: '#9ca3af',
  },
  devError: {
    marginTop: 24,
    padding: 12,
    textAlign: 'left',
    fontSize: 12,
    fontFamily: 'monospace',
    color: '#dc2626',
    backgroundColor: '#fef2f2',
    borderRadius: 8,
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
}
